// src/auth/authFailure.js
//
// Classifies a failed /auth/me call. Entra sign-in succeeding tells us nothing about
// whether THIS person may use THIS app, and the three ways that can go wrong need
// three different screens:
//
//   NOT_A_PERSON  The token carries no associateID claim. On the shared workstations
//                 these apps run on, this is almost always a generic departmental
//                 account (MiamiGrocery@fhiworks.com) that Windows is signed into —
//                 not a mistake the user made, just the wrong identity. The API
//                 raises UnauthorizedAccessException and returns 401 with a message.
//
//   NOT_AUTHORIZED  A real person, correctly identified, with no row in
//                 tblSecurityUsers. The API returns 403 (Forbid, empty body).
//
//   CONFIG        Anything else: the token was rejected outright, the API is down,
//                 a 500. These are for us, not the user.
//
// Kept as a pure function of the axios error so it can be unit-tested without a
// browser, MSAL, or a live API.

export const AuthFailure = {
  NOT_A_PERSON: 'NOT_A_PERSON',
  NOT_AUTHORIZED: 'NOT_AUTHORIZED',
  CONFIG: 'CONFIG',
};

/** Matches the API's "no associateID claim" message without depending on its exact wording. */
const NO_CLAIM_PATTERN = /associate\s*id\s*claim/i;

export function classifyAuthFailure(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  // 403 Forbid: authenticated and identified, just not granted access to this app.
  if (status === 403) return AuthFailure.NOT_AUTHORIZED;

  if (status === 401) {
    // A 401 carrying the API's own message is an authorization decision, not a token
    // problem — the middleware rejects bad tokens with an empty body before the
    // controller runs.
    if (typeof message === 'string' && NO_CLAIM_PATTERN.test(message)) {
      return AuthFailure.NOT_A_PERSON;
    }
    return AuthFailure.CONFIG;
  }

  return AuthFailure.CONFIG;
}

/**
 * Copy for the Access Denied screen. Written for the person standing at the machine,
 * not for whoever deployed it.
 */
export function authFailureCopy(failure, userEmail) {
  const account = userEmail ? `“${userEmail}”` : 'the account you used';

  switch (failure) {
    case AuthFailure.NOT_A_PERSON:
      return {
        title: 'Wrong account',
        body:
          `You are signed in as ${account}. That is a shared account, so the Lead Pay `
          + 'Portal cannot tell who is making entries. Sign in again with your own '
          + 'work account to continue.',
        actionLabel: 'Sign in with my account',
      };

    case AuthFailure.NOT_AUTHORIZED:
      return {
        title: 'Access denied',
        body:
          `${userEmail ? `You are signed in as ${account}, which is` : 'Your account is'} `
          + 'not set up for the Lead Pay Portal. Contact your system administrator if '
          + 'you believe this is an error.',
        actionLabel: 'Sign in with a different account',
      };

    default:
      return {
        title: 'Sign-in problem',
        body:
          'You signed in successfully, but the Lead Pay Portal could not verify your '
          + 'access. Please try again, and contact the help desk if it keeps happening.',
        actionLabel: 'Try again',
      };
  }
}
